import * as React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ArrowLeft, Check, Loader2, X } from "lucide-react";
import { ApiError } from "@eduviaos/shared";
import { Button, cn } from "@eduviaos/ui-kit";
import { ResultHero } from "../../components/motion/ResultHero";
import { Reveal, SectionCard } from "../../components/Section";
import type { ZiggyAge } from "../../lib/ziggy";
import { useSubmitDiagnostic } from "../../lib/learning/hooks";
import type {
  DiagnosticResult,
  DiagnosticSession,
  GradedQuestion,
  TopicGap,
} from "../../lib/learning/types";
import { usePortalLocale } from "../../lib/i18n/usePortalLocale";
import type { PortalStrings } from "../../lib/i18n/usePortalLocale";

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

/**
 * Runs one diagnostic quiz and shows what it found (Phase SC-3d).
 *
 * The session is created by the caller, not on mount here — generating a
 * quiz costs an AI call, so it stays tied to an explicit tap rather than
 * to a component being rendered.
 *
 * Answers are held locally until submit. The backend reveals the answer
 * key only in the submit response (`DiagnosticQuestionPublic` has no
 * `correct_option_id` at all), so per-question feedback before then is
 * not something the client is *choosing* to withhold — it doesn't have
 * it. That's the Testing Effect: retrieve first, find out after.
 */
export function DiagnosticRunner({
  session,
  ziggyAge = "grown",
  onClose,
  onFinished,
}: {
  session: DiagnosticSession;
  ziggyAge?: ZiggyAge;
  onClose: () => void;
  onFinished: (result: DiagnosticResult) => void;
}) {
  const reduce = useReducedMotion();
  const { strings: s } = usePortalLocale();
  const submitMut = useSubmitDiagnostic();

  const [index, setIndex] = React.useState(0);
  const [answers, setAnswers] = React.useState<Record<string, string | null>>({});
  const [result, setResult] = React.useState<DiagnosticResult | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const questions = session.questions;
  const total = questions.length;
  const question = questions[Math.min(index, total - 1)];
  const isLast = index >= total - 1;

  if (result) {
    return (
      <DiagnosticResultView
        result={result}
        session={session}
        ziggyAge={ziggyAge}
        strings={s}
        onDone={onClose}
      />
    );
  }

  if (!question) {
    return (
      <SectionCard className="space-y-3 text-center">
        <p className="text-sm font-semibold">{s.diagnosticNoQuestions}</p>
        <p className="text-muted-foreground text-xs">{s.diagnosticBuildFailed}</p>
        <Button variant="outline" onClick={onClose}>
          {s.back}
        </Button>
      </SectionCard>
    );
  }

  const answered = question.id in answers;
  const selected = answers[question.id] ?? null;
  const answeredCount = questions.filter((q) => answers[q.id]).length;
  const progress = Math.round(((index + 1) / total) * 100);

  async function submit() {
    setError(null);
    try {
      const payload = questions.map((q) => ({
        question_id: q.id,
        selected_option_id: answers[q.id] ?? null,
      }));
      const res = await submitMut.mutateAsync({ sessionId: session.id, answers: payload });
      setResult(res);
      onFinished(res);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 0 ? s.errorOffline : s.diagnosticScoringFailed,
      );
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => (index === 0 ? onClose() : setIndex((i) => i - 1))}
          disabled={submitMut.isPending}
          className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm font-medium transition-colors disabled:opacity-50"
        >
          <ArrowLeft className="size-4" />
          {s.back}
        </button>
        <span className="text-muted-foreground text-xs font-semibold">
          {index + 1} / {total}
        </span>
      </div>

      <div
        className="bg-muted h-2 overflow-hidden rounded-full"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={index + 1}
        aria-label={s.quizProgressAria(index + 1, total)}
      >
        <motion.div
          className="bg-primary h-full rounded-full"
          initial={false}
          animate={{ width: `${progress}%` }}
          transition={{ duration: reduce ? 0 : 0.3, ease: EASE_OUT }}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={question.id}
          initial={reduce ? false : { opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, x: -16 }}
          transition={{ duration: 0.24, ease: EASE_OUT }}
          className="space-y-4"
        >
          <SectionCard className="space-y-3">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="bg-primary/12 text-primary rounded-full px-2.5 py-0.5 text-[0.62rem] font-semibold uppercase tracking-wide">
                {question.subject}
              </span>
              <span className="text-muted-foreground text-xs">{question.topic}</span>
            </div>
            <p id={`q-${question.id}`} className="text-sm font-semibold leading-relaxed">
              {question.prompt}
            </p>
          </SectionCard>

          {/* Labelled by the visible prompt rather than an sr-only legend:
              a legend would read the question twice to a screen reader. */}
          <fieldset className="space-y-2" aria-labelledby={`q-${question.id}`}>
            {question.options.map((opt) => {
              const active = selected === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setAnswers((a) => ({ ...a, [question.id]: opt.id }))}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm transition-colors duration-150 active:scale-[0.99]",
                    active
                      ? "border-primary bg-primary/8 text-foreground font-semibold"
                      : "border-border/60 bg-card hover:bg-muted/50",
                  )}
                >
                  <span
                    className={cn(
                      "grid size-5 shrink-0 place-items-center rounded-full border",
                      active
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border",
                    )}
                  >
                    {active && <Check className="size-3" />}
                  </span>
                  <span className="min-w-0">{opt.label}</span>
                </button>
              );
            })}
          </fieldset>
        </motion.div>
      </AnimatePresence>

      {error && (
        <p
          role="alert"
          className="bg-destructive/10 text-destructive rounded-lg px-3 py-2 text-center text-sm font-medium"
        >
          {error}
        </p>
      )}

      <div className="flex items-center gap-2">
        {/* Skipping is explicit rather than implicit: a skip still grades
            as wrong, but the BE keeps it distinct from a guess, so the
            learner's history can tell "didn't know" from "didn't answer". */}
        <Button
          variant="ghost"
          className="flex-1"
          disabled={submitMut.isPending}
          onClick={() => {
            setAnswers((a) => ({ ...a, [question.id]: null }));
            if (!isLast) setIndex((i) => i + 1);
          }}
        >
          {s.skip}
        </Button>
        <Button
          className="flex-[2]"
          disabled={submitMut.isPending || (!isLast && !answered)}
          onClick={() => (isLast ? void submit() : setIndex((i) => i + 1))}
        >
          {submitMut.isPending ? (
            <>
              <Loader2 className="mr-1.5 size-4 animate-spin" /> {s.scoring}
            </>
          ) : isLast ? (
            s.finishWithAnswered(answeredCount, total)
          ) : (
            s.next
          )}
        </Button>
      </div>
    </div>
  );
}

/* ─────────────────────────────── results ─────────────────────────────── */

function DiagnosticResultView({
  result,
  session,
  ziggyAge,
  strings: s,
  onDone,
}: {
  result: DiagnosticResult;
  session: DiagnosticSession;
  ziggyAge: ZiggyAge;
  strings: PortalStrings;
  onDone: () => void;
}) {
  const pct = Math.round(result.score * 100);
  const scheduled = result.cards_created + result.cards_updated;
  const isStepQuiz = Boolean(session.leaf_id);
  const stepPassed = result.step_passed === true;
  const stepFailed = isStepQuiz && result.step_passed === false;

  const subtitle = isStepQuiz
    ? stepPassed
      ? result.step_unlocked
        ? s.diagnosticStepPassedUnlock
        : s.diagnosticStepPassedOnly
      : s.diagnosticStepFailedNeed70(pct)
    : scheduled > 0
      ? s.diagnosticTopicsQueued(scheduled)
      : s.diagnosticNothingToQueue;

  return (
    <div className="space-y-4">
      <ResultHero
        pose={
          isStepQuiz ? (stepPassed ? "excited" : "thinking") : pct >= 80 ? "excited" : "thinking"
        }
        ziggyAge={ziggyAge}
        title={
          isStepQuiz
            ? stepPassed
              ? s.diagnosticStepQualified
              : s.diagnosticNotQuiteYet
            : s.diagnosticScoreSummary(result.correct, result.asked)
        }
        subtitle={subtitle}
      />

      {stepFailed && (
        <SectionCard className="border-joy-amber/30 bg-joy-amber/5 p-4 text-center">
          <p className="text-sm font-medium">{s.diagnosticReviewStepRetry}</p>
        </SectionCard>
      )}

      {!isStepQuiz && result.topic_gaps.length > 0 && (
        <section className="space-y-2">
          <Reveal>
            <h3 className="font-display text-base font-semibold">{s.diagnosticWhereToFocus}</h3>
          </Reveal>
          {result.topic_gaps.map((gap, i) => (
            <Reveal key={`${gap.subject}:${gap.topic}`} index={i + 1}>
              <GapRow gap={gap} strings={s} />
            </Reveal>
          ))}
        </section>
      )}

      <section className="space-y-2">
        <Reveal index={result.topic_gaps.length + 1}>
          <h3 className="font-display text-base font-semibold">{s.diagnosticYourAnswers}</h3>
        </Reveal>
        {result.questions.map((q, i) => (
          <Reveal key={q.id} index={result.topic_gaps.length + 2 + i}>
            <GradedRow question={q} strings={s} />
          </Reveal>
        ))}
      </section>

      <Reveal index={result.topic_gaps.length + result.questions.length + 3}>
        <Button className="w-full active:scale-[0.97]" onClick={onDone}>
          {isStepQuiz
            ? stepPassed
              ? s.diagnosticBackToRoadmap
              : s.diagnosticTryAgainLater
            : s.done}
        </Button>
      </Reveal>
    </div>
  );
}

function GapRow({ gap, strings: s }: { gap: TopicGap; strings: PortalStrings }) {
  const pct = Math.round(gap.accuracy * 100);
  return (
    <SectionCard className="flex items-center gap-3 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{gap.topic}</p>
        <p className="text-muted-foreground text-xs">{gap.subject}</p>
      </div>
      <div className="w-24 shrink-0">
        <div className="bg-muted h-1.5 overflow-hidden rounded-full">
          <div
            className={cn("h-full rounded-full", pct >= 80 ? "bg-joy-green" : "bg-joy-amber")}
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="text-muted-foreground mt-1 text-right text-[0.68rem] tabular-nums">
          {s.gapRight(gap.correct, gap.asked)}
        </p>
      </div>
    </SectionCard>
  );
}

function GradedRow({ question, strings: s }: { question: GradedQuestion; strings: PortalStrings }) {
  const correctLabel =
    question.options.find((o) => o.id === question.correct_option_id)?.label ?? "—";
  const chosenLabel = question.selected_option_id
    ? (question.options.find((o) => o.id === question.selected_option_id)?.label ?? "—")
    : null;

  return (
    <SectionCard className="space-y-2">
      <div className="flex items-start gap-2.5">
        <span
          className={cn(
            "mt-0.5 grid size-5 shrink-0 place-items-center rounded-full",
            question.is_correct
              ? "bg-joy-green/15 text-joy-green"
              : "bg-destructive/10 text-destructive",
          )}
        >
          {question.is_correct ? <Check className="size-3" /> : <X className="size-3" />}
        </span>
        <p className="min-w-0 text-sm font-medium leading-relaxed">{question.prompt}</p>
      </div>

      {!question.is_correct && (
        <p className="text-muted-foreground pl-7.5 text-xs">
          {chosenLabel ? (
            <>
              {s.diagnosticYouPicked}{" "}
              <span className="text-foreground font-medium">{chosenLabel}</span> —
            </>
          ) : (
            <>{s.diagnosticSkipped} </>
          )}{" "}
          {s.diagnosticAnswerIs} <span className="text-foreground font-medium">{correctLabel}</span>
          .
        </p>
      )}

      {question.explanation && (
        <p className="text-muted-foreground pl-7.5 text-xs leading-relaxed">
          {question.explanation}
        </p>
      )}
    </SectionCard>
  );
}
